// Risk (SM-712 / SM-768): the node's exposure picture, READ-ONLY —
// brokers never configure their own limit (SM-768 §5); the exchange does.
// The headline state is the server's own flags (blocked, then warning at
// warning_threshold_bps), so "near the limit" is said exactly when the
// server would start warning.
//
// Always in GOLD, whatever the shell's currency: exposure exists only for
// external-credit (THIRD_PARTY_CREDIT) accounts, which are GOLD only, and
// broker-service answers 400 to any other currency (SM-768 B7). The page
// says so in one line instead of sending the refused read (SM-848).
//
//   GET /api/broker/{id}/exposure?currency=GOLD   ExposureView
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Money } from "@/components/ui/Money";
import { KeyValues, Stat, Time } from "@/components/ui/Common";
import { QueryBlock } from "@/components/ui/States";
import { Badge } from "@/components/ui/Badge";
import { EXPOSURE_CURRENCY } from "@/lib/currency";
import { fetchExposure, keys } from "@/lib/queries";
import { exposureLevel, utilisationWidth } from "@/lib/status";
import { formatBps } from "@/lib/units";
import type { BrokerBreadcrumbItem, ExposureView } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

export const Route = createFileRoute("/_app/risk")({
  beforeLoad: capabilityGuard("VIEW"),
  component: RiskPage,
});

function RiskPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId } = useScope();
  const probe = { queryKey: keys.exposure(nodeId), queryFn: () => fetchExposure(nodeId) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow", refetchInterval: 60_000 });
  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("risk.title")} subtitle={t("risk.subtitle")} coverage="split" money currency={EXPOSURE_CURRENCY} />
      <p className="mb-4 text-xs text-t-3">{t("risk.goldOnly")}</p>
      <QueryBlock query={q} lines={6}>
        {(v) => <Exposure v={v} />}
      </QueryBlock>
    </Guarded>
  );
}

function Exposure({ v }: { v: ExposureView }) {
  const { t, formatNumber } = useI18n();
  const { nodeId, path } = useScope();
  const c = v.currency;
  const binding = bindingLine(t, v.binding_broker_id, nodeId, path.path);
  const level = exposureLevel(v);
  const width = utilisationWidth(v.utilisation_bps);
  const warnAt = utilisationWidth(v.warning_threshold_bps);
  return (
    <div className="space-y-4">
      <div
        className={level === "blocked" ? "banner danger" : level === "warning" ? "banner warn" : "banner"}
        role={level === "blocked" || level === "warning" ? "alert" : "status"}
      >
        <span aria-hidden>{level === "ok" || level === "no-limit" ? "✓" : "⚠"}</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-t-1">{t(`risk.level.${level}`)}</p>
          {(v.blocked_reasons ?? []).length > 0 && (
            <p className="mt-1 text-xs text-t-2">
              {t("risk.blockedBecause", {
                reasons: (v.blocked_reasons ?? []).map((r) => labelOf(t, "risk.blockReason", r)).join(", "),
              })}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={t("risk.ownExposure")} sub={<SubLines lines={[t("risk.ownExposureSub"), t("risk.openEntriesCount", { count: formatNumber(v.open_entries) })]} />}>
          <Money currency={c} value={v.own_exposure} />
        </Stat>
        <Stat
          label={t("risk.subtreeExposure")}
          sub={
            <SubLines
              lines={[
                t("risk.subtreeExposureSub"),
                v.subtree_open_entries !== undefined && t("risk.openEntriesCount", { count: formatNumber(v.subtree_open_entries) }),
              ]}
            />
          }
        >
          <Money currency={c} value={v.subtree_exposure} />
        </Stat>
        <Stat label={t("risk.collateral")} sub={t("risk.collateralSub")}>
          <Money currency={c} value={v.collateral} />
        </Stat>
        <Stat label={t("risk.effectiveHeadroom")} sub={binding}>
          {v.effective_headroom ? <Money currency={c} value={v.effective_headroom} /> : "—"}
        </Stat>
      </div>

      <SectionCard title={t("risk.limitTitle")}>
        {v.has_own_limit ? (
          <div className="space-y-3">
            <div>
              <div className="mb-1 flex flex-wrap justify-between gap-2 text-xs text-t-2">
                <span>{t("risk.utilisation")}</span>
                <span className="num">
                  {t("risk.utilisationOf", { used: formatBps(v.utilisation_bps ?? 0), warn: formatBps(v.warning_threshold_bps ?? 0) })}
                </span>
              </div>
              <div
                className="relative h-3 overflow-hidden rounded-full bg-bg-3"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(width)}
                aria-label={t("risk.utilisation")}
              >
                <div
                  className={level === "blocked" ? "h-full bg-no" : level === "warning" ? "h-full bg-warn" : "h-full bg-neon"}
                  style={{ width: `${width}%` }}
                />
                {warnAt > 0 && <div className="absolute inset-y-0 w-0.5 bg-t-1/60" style={{ left: `${warnAt}%` }} aria-hidden />}
              </div>
            </div>
            <KeyValues
              rows={[
                { label: t("risk.limit"), value: <Money currency={c} value={v.limit} /> },
                { label: t("risk.headroom"), value: <Money currency={c} value={v.headroom} /> },
                { label: t("risk.allowance"), value: v.allowance ? <Money currency={c} value={v.allowance} /> : "—" },
                { label: t("risk.allowanceExpires"), value: <Time ms={v.allowance_expires_at_unix_ms} /> },
              ]}
            />
          </div>
        ) : (
          <p className="text-sm text-t-3">{t("risk.noOwnLimitBody")}</p>
        )}
      </SectionCard>

      {(v.net_position !== undefined || v.receivables_open > 0) && (
        <SectionCard title={t("risk.settlementTitle")}>
          <KeyValues
            rows={[
              { label: t("risk.netPosition"), value: v.net_position !== undefined ? <Money currency={c} value={v.net_position} tone="auto" /> : "—" },
              { label: t("risk.owed"), value: v.owed ? <Money currency={c} value={v.owed} /> : "—", hint: t("risk.owedHint") },
              // AssessShortfall subtracts the ROOT's collateral; a non-root
              // view's v.collateral is its own subtree's, so it is shown
              // only where it is the figure subtracted
              ...(v.net_position !== undefined ? [{ label: t("risk.collateral"), value: <Money currency={c} value={v.collateral} /> }] : []),
              {
                label: t("risk.coveredByResolutions"),
                value: v.covered_by_resolutions ? <Money currency={c} value={v.covered_by_resolutions} /> : "—",
                hint: t("risk.coveredByResolutionsHint"),
              },
              { label: t("risk.uncovered"), value: v.uncovered ? <Money currency={c} value={v.uncovered} /> : "—", hint: t("risk.uncoveredHint") },
              {
                label: t("risk.receivablesOpen"),
                value: (
                  <span className="inline-flex items-center gap-2">
                    <Badge tone={v.receivables_open > 0 ? "warn" : "neutral"}>{String(v.receivables_open)}</Badge>
                    {v.receivable_open_amount && <Money currency={c} value={v.receivable_open_amount} />}
                  </span>
                ),
              },
            ]}
          />
          <p className="mt-2 text-2xs text-t-3">{t("risk.netPositionNote")}</p>
        </SectionCard>
      )}
      <p className="text-2xs text-t-3">
        {t("common.asOf")} <Time ms={v.as_of_unix_ms} />
      </p>
    </div>
  );
}

/**
 * Who binds the effective headroom, by name: this node, a node on the
 * caller's breadcrumb, or — the breadcrumb leaves out ancestors above the
 * caller's membership on purpose — "a node above your access". Never the
 * raw id, which would disclose a node the portal otherwise hides.
 */
function bindingLine(t: ReturnType<typeof useI18n>["t"], bindingId: string | undefined, nodeId: string, crumbs: BrokerBreadcrumbItem[]) {
  if (!bindingId) return undefined;
  if (bindingId === nodeId) return t("risk.bindingSelf");
  const crumb = crumbs.find((b) => b.broker_id === bindingId);
  return crumb ? t("risk.bindingNode", { name: crumb.display_name || crumb.broker_id }) : t("risk.bindingAbove");
}

function SubLines({ lines }: { lines: (string | false)[] }) {
  return (
    <>
      {lines.filter((l): l is string => Boolean(l)).map((l) => (
        <span key={l} className="block">
          {l}
        </span>
      ))}
    </>
  );
}
